<template>
  <div v-if="privilage() == 'admin'" class="page-container">
    <div class="page-header">
      <div>
        <h1>Masse Salariale</h1>
        <p class="page-subtitle">Vue d'ensemble des salaires et avances</p>
      </div>
      <div class="month-pick">
        <label class="form-label">Sélectionner un mois</label>
        <MonthPickerButton v-model="selectedMonth" @change="fetchStaffData" />
      </div>
    </div>

    <!-- Summary Cards -->
    <div v-if="staffList.length" class="salary-summary">
      <div class="summary-card summary-blue">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ staffList.length }}</div>
          <div class="summary-label">Employés</div>
        </div>
      </div>
      <div class="summary-card summary-primary">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ totalSalary.toFixed(2) }} DH</div>
          <div class="summary-label">Masse salariale</div>
        </div>
      </div>
      <div class="summary-card summary-amber">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ totalAdvance.toFixed(2) }} DH</div>
          <div class="summary-label">Total avances</div>
        </div>
      </div>
      <div class="summary-card summary-orange">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ totalPrime.toFixed(2) }} DH</div>
          <div class="summary-label">Total primes</div>
        </div>
      </div>
      <div class="summary-card summary-red">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ totalRetenue.toFixed(2) }} DH</div>
          <div class="summary-label">Total retenues</div>
        </div>
      </div>
      <div class="summary-card summary-red">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ totalCarryOver.toFixed(2) }} DH</div>
          <div class="summary-label">Reports négatifs</div>
        </div>
      </div>
      <div class="summary-card summary-green">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ totalReste.toFixed(2) }} DH</div>
          <div class="summary-label">Reste à régler</div>
        </div>
      </div>
      <div class="summary-card summary-blue">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ totalSettled.toFixed(2) }} DH</div>
          <div class="summary-label">Déjà réglé</div>
        </div>
      </div>
      <div class="summary-card summary-red">
        <div class="summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
        </div>
        <div>
          <div class="summary-value">{{ totalAdvanceRecover.toFixed(2) }} DH</div>
          <div class="summary-label">À récupérer</div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div v-if="staffList.length" class="table-card">
      <div class="table-head-bar">
        <h3>Détail par employé</h3>
        <span class="badge-count">{{ staffList.length }} employé{{ staffList.length > 1 ? 's' : '' }}</span>
      </div>
      <div class="table-responsive">
        <table class="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Employé</th>
              <th>Jours</th>
              <th>Jours RH payés</th>
              <th>Contrôle heures</th>
              <th>Salaire base</th>
              <th>Salaire/jour</th>
              <th>Salaire calculé</th>
              <th>Avances</th>
              <th>Primes</th>
              <th>Report</th>
              <th>Retenues</th>
              <th>Net mois</th>
              <th>Reste</th>
              <th>Réglé</th>
              <th>À récupérer</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(staff, index) in staffList" :key="staff.id">
              <td><span class="row-num">{{ index + 1 }}</span></td>
              <td>
                <div class="staff-name-cell">
                  <div class="staff-photo">
                    <img v-if="staff.pic" :src="staffImage(staff)" :alt="staff.first_name" />
                    <span v-else>{{ staffInitials(staff) }}</span>
                  </div>
                  <span>{{ staff.first_name }} {{ staff.last_name }}</span>
                </div>
              </td>
              <td><span class="amount-chip chip-purple">{{ staff.days_worked }} j</span></td>
              <td><span class="amount-chip chip-info">{{ staff.paid_days || 0 }} j</span></td>
              <td>
                <span class="amount-chip chip-teal">{{ (staff.total_hours || 0).toFixed(2) }} h</span>
              </td>
              <td><span class="amount-chip chip-blue">{{ staff.salary.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-info">{{ (staff.salary / 30).toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-info">{{ (staff.earned_salary ?? ((staff.days_worked || 0) * (staff.salary / 30))).toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-amber">{{ staff.total_avance.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-orange">{{ staff.total_prime.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-red-soft">{{ (staff.previous_debt ?? staff.carry_over ?? 0).toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-red-soft">{{ (staff.total_retenue || 0).toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-blue">{{ (staff.month_net ?? staff.amount_due ?? 0).toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-green">{{ (staff.reste_a_regler ?? staff.remaining_to_pay ?? Math.max(staff.reste || 0, 0)).toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-blue">{{ (staff.total_settled || 0).toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-red-soft">{{ (staff.advance_recovery_remaining ?? staff.advance_to_recover ?? 0).toFixed(2) }} DH</span></td>
              <td><span class="status-chip" :class="statusClass(staff.salary_status)">{{ statusLabel(staff.salary_status) }}</span></td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2" style="text-align:right; font-weight:700;">Totaux</td>
              <td><span class="amount-chip chip-purple">{{ totalDaysWorked }} j</span></td>
              <td><span class="amount-chip chip-info">{{ totalPaidDays }} j</span></td>
              <td>
                <span class="amount-chip chip-teal">{{ totalHoursWorked.toFixed(2) }} h</span>
              </td>
              <td><span class="amount-chip chip-blue">{{ totalSalary.toFixed(2) }} DH</span></td>
              <td></td>
              <td><span class="amount-chip chip-info">{{ totalSalaryCalculated.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-amber">{{ totalAdvance.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-orange">{{ totalPrime.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-red-soft">{{ totalCarryOver.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-red-soft">{{ totalRetenue.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-blue">{{ totalDue.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-green">{{ totalReste.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-blue">{{ totalSettled.toFixed(2) }} DH</span></td>
              <td><span class="amount-chip chip-red-soft">{{ totalAdvanceRecover.toFixed(2) }} DH</span></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div v-else-if="selectedMonth" class="state-view">
      <div class="state-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
      </div>
      <p>Aucune donnée pour ce mois</p>
    </div>
  </div>
</template>

<script>
import MonthPickerButton from './MonthPickerButton.vue';

export default {
  inject: ['privilage'],
  name: 'StaffTable',
  components: { MonthPickerButton },
  data() {
    return { selectedMonth: null, staffList: [] };
  },
  computed: {
    totalDaysWorked() { return this.staffList.reduce((s, st) => s + st.days_worked, 0); },
    totalHoursWorked() { return this.staffList.reduce((s, st) => s + (st.total_hours || 0), 0); },
    totalPaidDays() { return this.staffList.reduce((s, st) => s + (st.paid_days || 0), 0); },
    totalSalary() { return this.staffList.reduce((s, st) => s + st.salary, 0); },
    totalSalaryCalculated() { return this.staffList.reduce((s, st) => s + (st.earned_salary ?? ((st.days_worked || 0) * (st.salary / 30))), 0); },
    totalAdvance() { return this.staffList.reduce((s, st) => s + st.total_avance, 0); },
    totalPrime() { return this.staffList.reduce((s, st) => s + (st.total_prime || 0), 0); },
    totalCarryOver() { return this.staffList.reduce((s, st) => s + (st.previous_debt ?? st.carry_over ?? 0), 0); },
    totalRetenue() { return this.staffList.reduce((s, st) => s + (st.total_retenue || 0), 0); },
    totalDue() { return this.staffList.reduce((s, st) => s + (st.month_net ?? st.amount_due ?? 0), 0); },
    totalReste() { return this.staffList.reduce((s, st) => s + (st.reste_a_regler ?? st.remaining_to_pay ?? Math.max(st.reste || 0, 0)), 0); },
    totalSettled() { return this.staffList.reduce((s, st) => s + (st.total_settled || 0), 0); },
    totalAdvanceRecover() { return this.staffList.reduce((s, st) => s + (st.advance_recovery_remaining ?? st.advance_to_recover ?? 0), 0); }
  },
  methods: {
    statusLabel(status) {
      const labels = {
        non_regle: 'Non réglé',
        partiellement_regle: 'Partiel',
        regle: 'Réglé',
        rien_a_regler: 'Rien à régler',
        a_recuperer: 'À récupérer',
        partiellement_recupere: 'Récupération partielle',
        recupere: 'Récupéré'
      };
      return labels[status] || 'Non réglé';
    },
    statusClass(status) {
      const classes = {
        non_regle: 'status-debt',
        partiellement_regle: 'status-warn',
        regle: 'status-ok',
        rien_a_regler: 'status-info',
        a_recuperer: 'status-debt',
        partiellement_recupere: 'status-warn',
        recupere: 'status-ok'
      };
      return classes[status] || 'status-info';
    },
    staffInitials(staff) {
      return `${staff.first_name?.[0] || ''}${staff.last_name?.[0] || ''}` || '?';
    },
    staffImage(staff) {
      if (!staff.pic) return '';
      return staff.pic.startsWith('/api-pointage/uploads/') ? staff.pic : `/api-pointage/uploads/${staff.pic}`;
    },
    async fetchStaffData() {
      try {
        const response = await fetch(`/api-pointage/salarymass?month=${this.selectedMonth}`);
        if (!response.ok) throw new Error('Error');
        const data = await response.json();
        this.staffList = Array.isArray(data) ? data : [];
      } catch { this.staffList = []; }
    },
  },
  mounted() {
    const d = new Date();
    this.selectedMonth = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    this.fetchStaffData();
  }
};
</script>

<style scoped>
.page-container { display: flex; flex-direction: column; gap: 14px; animation: slideUp 0.25s ease; }
.page-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-header h1 { font-size: 24px; margin-bottom: 4px; }
.page-subtitle { font-size: 13px; color: var(--gray-500); }
.month-pick { display: flex; flex-direction: column; gap: 4px; }

.salary-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.summary-card { background: var(--white); border: 1px solid var(--border); border-radius: 8px; padding: 12px 14px; display: flex; align-items: center; gap: 10px; box-shadow: var(--shadow-sm); }
.summary-icon { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.summary-icon svg { width: 18px; height: 18px; }
.summary-blue .summary-icon { background: rgba(59,130,246,0.1); color: var(--info); }
.summary-primary .summary-icon { background: var(--primary-glow); color: var(--primary); }
.summary-amber .summary-icon { background: var(--warning-bg); color: var(--warning); }
.summary-orange .summary-icon { background: rgba(249,115,22,0.1); color: #f97316; }
.summary-red .summary-icon { background: rgba(239,68,68,0.12); color: #dc2626; }
.summary-green .summary-icon { background: var(--success-bg); color: var(--success); }
.summary-value { font-family: 'DM Sans', sans-serif; font-size: 20px; font-weight: 900; color: var(--gray-900); }
.value-negative { color: #dc2626; }
.summary-label { font-size: 12px; color: var(--gray-500); margin-top: 2px; }

.table-card { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.table-head-bar { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid var(--border); }
.table-head-bar h3 { font-size: 15px; }
.badge-count { background: var(--gray-100); color: var(--gray-600); padding: 3px 10px; border-radius: var(--radius-full); font-size: 12px; font-weight: 700; }
.table-responsive { overflow-x: auto; }
.staff-name-cell { display: flex; align-items: center; gap: 10px; min-width: 170px; }
.staff-photo { width: 42px; height: 42px; background: var(--primary-glow); border: 1px solid var(--border); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 900; color: var(--primary); flex-shrink: 0; text-transform: uppercase; overflow: hidden; }
.staff-photo img { width: 100%; height: 100%; object-fit: cover; }

.row-num { display: inline-flex; width: 24px; height: 24px; align-items: center; justify-content: center; background: var(--gray-100); border-radius: 50%; font-size: 11px; font-weight: 700; color: var(--gray-500); }
.amount-chip { display: inline-flex; padding: 4px 12px; border-radius: 5px; font-size: 13px; font-weight: 700; }
.hours-stack { display: inline-flex; flex-direction: column; align-items: flex-start; gap: 4px; }
.hours-stack small { color: var(--gray-500); font-size: 11px; font-weight: 700; white-space: nowrap; }
.chip-blue { background: var(--primary-glow); color: var(--primary); }
.chip-purple { background: rgba(168,85,247,0.1); color: #a855f7; }
.chip-info { background: rgba(14,165,233,0.1); color: #0ea5e9; }
.chip-amber { background: var(--warning-bg); color: var(--warning); }
.chip-orange { background: rgba(249,115,22,0.1); color: #f97316; }
.chip-green { background: var(--success-bg); color: var(--success); }
.chip-red-soft { background: rgba(248,113,113,0.12); color: #b91c1c; }
.chip-red { background: rgba(239,68,68,0.14); color: #dc2626; }

.status-chip { display: inline-flex; padding: 4px 10px; border-radius: 5px; font-size: 11.5px; font-weight: 700; }
.status-ok { background: var(--success-bg); color: var(--success); }
.status-info { background: rgba(14,165,233,0.1); color: #0ea5e9; }
.status-warn { background: var(--warning-bg); color: var(--warning); }
.status-debt { background: rgba(239,68,68,0.14); color: #dc2626; }

.state-view { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 32px; gap: 12px; color: var(--gray-400); font-size: 14px; }
.state-icon { width: 48px; height: 48px; background: var(--gray-100); border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.state-icon svg { width: 28px; height: 28px; }

@media (max-width: 900px) { .salary-summary { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { 
  .salary-summary { grid-template-columns: 1fr; }
  .page-header { flex-direction: column; align-items: flex-start; padding-left: 56px; }
}
</style>
